import React, { useState, useEffect, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Text } from '@cuemath/web-comps';
import { COLORS } from '@cuemath/comps';
import styled from 'styled-components';

import TrialTable from './trial-table';
import RegularClassTable from './regular-class-table';
import { getTrialClassrooms } from '../../api/trial-analytics';
import { transformTrialData } from '../../utils/trial-data-transformer';
import { useTrialPermissions } from '../../hooks/useTrialDBHelper';
import { safeSessionGet } from '../../utils/classroom-helpers';
import { trackEvent } from 'modules/analytics';
import { EVENT_TYPE } from 'constants/logger';

export const TABS = {
  TRIAL: 'trial',
  REGULAR: 'regular',
};

const Container = styled.div`
  width: inherit;
  height: inherit;
  margin: 0 auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  margin-bottom: 24px;
`;

const TabBar = styled.div`
  display: flex;
  gap: 4px;
  background: ${COLORS.PALE_GREY};
  border-radius: 8px;
  padding: 4px;
  width: fit-content;
`;

const Tab = styled.div`
  padding: 8px 24px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: ${({ $active }) => ($active ? COLORS.WHITE : COLORS.GRAY)};
  background: ${({ $active }) => ($active ? COLORS.COLA : 'transparent')};
  border-radius: 6px;
  transition: all 0.2s ease;

  &:hover {
    color: ${({ $active }) => ($active ? COLORS.WHITE : COLORS.COLA)};
    background: ${({ $active }) => ($active ? COLORS.COLA : COLORS.PALE_GREY)};
  }
`;

const TabContent = styled.div`
  margin-top: 24px;
`;

const ErrorWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: red;
`;

const DASHBOARD_STATE_KEY = 'trial_dashboard_state';

function ClassroomDashboard() {
  const history = useHistory();
  const location = useLocation();
  const { canViewRegularClasses } = useTrialPermissions();

  // Restore active tab from location state (when returning from classroom-view)
  const initialTab = location.state?.activeTab || TABS.TRIAL;

  const [activeTab, setActiveTab] = useState(initialTab);

  // --- Trial tab state ---
  const restoredState = safeSessionGet(DASHBOARD_STATE_KEY);

  const [trials, setTrials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [countData, setCountData] = useState(0);
  const [idFilter, setIdFilter] = useState(restoredState?.idFilter || {});
  const [filters, setFilters] = useState(restoredState?.filters || {});
  const [perPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(restoredState?.currentPage || 1);

  const fetchTrials = useCallback(
    async (page = 1, filterParams = {}) => {
      setLoading(true);
      setError(null);

      try {
        const params = {
          page,
          per_page: perPage,
          ...filterParams,
        };

        const rawResponse = await getTrialClassrooms(params);
        const response = rawResponse.value || rawResponse;
        const trialsArray = response.data || [];
        const transformedTrials = transformTrialData(trialsArray);
        const totalCount = response.pagination?.total || trialsArray.length;

        setTrials(transformedTrials);
        setCountData(totalCount);
        setCurrentPage(page);
      } catch (err) {
        setError('Failed to fetch trials data. Please try again.');
        setTrials([]);
        setCountData(0);
      } finally {
        setLoading(false);
      }
    },
    [perPage],
  );

  useEffect(() => {
    trackEvent(EVENT_TYPE.TRIAL_DASHBOARD_VIEW);

    if (restoredState) {
      fetchTrials(restoredState.currentPage, {
        ...(restoredState.filters || {}),
        ...(restoredState.idFilter || {}),
      });
      sessionStorage.removeItem(DASHBOARD_STATE_KEY);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = useCallback(
    ({ type, value }) => {
      const newIdFilter = value ? { [type]: value } : {};
      setIdFilter(newIdFilter);
      if (!value) {
        setTrials([]);
        setCountData(0);
        setError(null);
        return;
      }
      trackEvent(EVENT_TYPE.TRIAL_DASHBOARD_SEARCH, {
        filter_type: type,
        has_filters: Object.keys(filters).filter(k => filters[k]).length > 0,
      });
      fetchTrials(1, { ...filters, ...newIdFilter });
    },
    [fetchTrials, filters],
  );

  const handleFilter = useCallback(
    filterParams => {
      trackEvent(EVENT_TYPE.TRIAL_DASHBOARD_FILTER_APPLIED, {
        filters: filterParams,
        filter_count: Object.keys(filterParams).filter(k => filterParams[k]).length,
      });
      setFilters(filterParams);
      fetchTrials(1, { ...filterParams, ...idFilter });
    },
    [fetchTrials, idFilter],
  );

  const handlePageChange = useCallback(
    page => {
      fetchTrials(page, { ...filters, ...idFilter });
    },
    [fetchTrials, filters, idFilter],
  );

  const handleRefresh = useCallback(() => {
    fetchTrials(currentPage, { ...filters, ...idFilter });
  }, [fetchTrials, currentPage, filters, idFilter]);

  const handleViewTrial = useCallback(
    trial => {
      if (trial.teacherClassroomId) {
        trackEvent(EVENT_TYPE.TRIAL_DASHBOARD_VIEW_TRIAL_CLICKED, {
          teacher_classroom_id: trial.teacherClassroomId,
          student_id: trial.studentId,
          tutor_id: trial.tutorId,
          region: trial.region,
          subject: trial.subject,
          grade: trial.grade,
        });

        const dashboardState = {
          currentPage,
          idFilter,
          filters,
        };
        sessionStorage.setItem(DASHBOARD_STATE_KEY, JSON.stringify(dashboardState));

        history.push(`/annotate/${trial.teacherClassroomId}`, { trial });
      } else {
        alert('Cannot view this trial: No classroom ID available');
      }
    },
    [history, currentPage, idFilter, filters],
  );

  const handleTabChange = tab => {
    setActiveTab(tab);
    if (tab === TABS.REGULAR) {
      trackEvent(EVENT_TYPE.CLASSROOM_DASHBOARD_VIEW);
    }
  };

  const renderTrialContent = () => {
    if (error) {
      return (
        <ErrorWrapper>
          <Text variant="body1Regular">{error}</Text>
        </ErrorWrapper>
      );
    }

    return (
      <TrialTable
        trials={trials}
        onSearch={handleSearch}
        onFilter={handleFilter}
        onPageChange={handlePageChange}
        countData={countData}
        loading={loading}
        currentPage={currentPage}
        filters={filters}
        idFilter={idFilter}
        onViewTrial={handleViewTrial}
        onRefresh={handleRefresh}
      />
    );
  };

  return (
    <Container>
      <Header>
        <Text variant="h4">Classroom Analysis Hub</Text>
      </Header>
      <TabBar>
        <Tab $active={activeTab === TABS.TRIAL} onClick={() => handleTabChange(TABS.TRIAL)}>
          Trial Classes
        </Tab>
        {canViewRegularClasses && (
          <Tab
            $active={activeTab === TABS.REGULAR}
            onClick={() => handleTabChange(TABS.REGULAR)}
          >
            Regular Classes
          </Tab>
        )}
      </TabBar>
      <TabContent>
        {activeTab === TABS.TRIAL && renderTrialContent()}
        {activeTab === TABS.REGULAR && canViewRegularClasses && <RegularClassTable />}
      </TabContent>
    </Container>
  );
}

export default ClassroomDashboard;
